import { getModalManager } from "@UI/ModalManager/index.js";
import {
  createListItem,
  type ListItemAPI,
  type ListItemProps,
} from "./ListItem.ts";
import "./List.scss"; // Co-located styles per repo standards [[memory:8094583]]

export interface ListHeaderConfig {
  readonly searchable?: boolean;
  readonly onSearchChange?: (query: string) => void;
  readonly onAddNew?: () => void;
}

export interface ListFooterConfig {
  readonly render: (container: HTMLElement) => void;
}

export interface ListConfig<T> {
  readonly items: readonly T[];
  readonly selectedId?: string | null;
  readonly header?: ListHeaderConfig;
  readonly footer?: ListFooterConfig;
  readonly onSelect?: (id: string) => void;
  readonly renderItem?: (container: HTMLElement, item: T) => ListItemAPI;
  readonly mapItem?: (item: T) => ListItemProps;
  readonly className?: string;
}

export interface ListAPI {
  readonly getElement: () => HTMLElement;
  readonly setItems: (items: readonly any[]) => void;
  readonly setSelectedId: (id: string | null) => void;
  readonly getSelectedId: () => string | null;
  readonly focusFirst: () => void;
  readonly destroy: () => void;
}

export const createList = <T>(config: ListConfig<T>): ListAPI => {
  const {
    items,
    selectedId = null,
    header,
    footer,
    onSelect,
    renderItem,
    mapItem,
    className = "",
  } = config;

  let isDestroyed = false;
  let currentItems: readonly T[] = items;
  let selected: string | null = selectedId;
  let itemAPIs: ListItemAPI[] = [];

  const modalManager = getModalManager();

  const root = document.createElement("div");
  root.className = `list ${className}`.trim();

  const headerEl = document.createElement("div");
  headerEl.className = "list__header";
  const itemsEl = document.createElement("div");
  itemsEl.className = "list__items";
  const footerEl = document.createElement("div");
  footerEl.className = "list__footer";

  const buildHeader = (): boolean => {
    headerEl.innerHTML = "";
    if (!header) {
      headerEl.style.display = "none";
      return false;
    }

    let hasContent = false;

    if (header.searchable) {
      const searchWrap = document.createElement("div");
      searchWrap.className = "list__search";
      const input = document.createElement("input");
      input.type = "text";
      input.placeholder = "Search";
      input.addEventListener("input", () =>
        header.onSearchChange?.(input.value)
      );
      searchWrap.appendChild(input);
      headerEl.appendChild(searchWrap);
      hasContent = true;
    }

    if (header.onAddNew) {
      const addBtn = document.createElement("button");
      addBtn.className = "list__add";
      addBtn.textContent = "+ Add";
      addBtn.addEventListener("click", () => header.onAddNew?.());
      headerEl.appendChild(addBtn);
      hasContent = true;
    }

    headerEl.style.display = hasContent ? "" : "none";
    return hasContent;
  };

  const handleSelect = (id: string): void => {
    selected = id;
    onSelect?.(id);
    // Keep list open if used inside a modal browser; parent controls closing
  };

  const renderItems = (): void => {
    // Clear old
    itemAPIs.forEach((api) => api.destroy());
    itemAPIs = [];
    itemsEl.innerHTML = "";

    currentItems.forEach((item) => {
      let api: ListItemAPI;
      if (renderItem) {
        const container = document.createElement("div");
        api = renderItem(container, item);
      } else if (mapItem) {
        const props = mapItem(item);
        const merged = { ...props, onSelect: handleSelect } as any;
        api = createListItem(merged);
      } else {
        throw new Error("List requires either renderItem or mapItem");
      }

      // Select state sync
      if ((api as any).setSelected && (api as any).update) {
        api.setSelected((propsFromItem(item)?.id ?? null) === selected);
      }

      itemsEl.appendChild(api.getElement());
      itemAPIs.push(api);
    });
  };

  const propsFromItem = (item: T): ListItemProps | null => {
    if (!mapItem) return null;
    return mapItem(item);
  };

  const focusFirst = (): void => {
    const first = itemAPIs[0];
    first?.focus();
  };

  const setItems = (items: readonly T[]): void => {
    currentItems = items;
    renderItems();
  };

  const setSelectedId = (id: string | null): void => {
    selected = id;
    itemAPIs.forEach((api) => api.setSelected(false));
    if (!id) return;
    // Try to focus/select matching id if available
    currentItems.forEach((item, index) => {
      const p = propsFromItem(item);
      if (p && p.id === id) {
        itemAPIs[index]?.setSelected(true);
      }
    });
  };

  const getSelectedId = (): string | null => selected;

  const handleKey = (e: KeyboardEvent): void => {
    if (isDestroyed) return;
    const currentIndex = itemAPIs.findIndex((api) =>
      (api as any).getElement()?.matches?.(".list-item--selected")
    );

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(
        (currentIndex < 0 ? -1 : currentIndex) + 1,
        itemAPIs.length - 1
      );
      itemAPIs.forEach((api) => api.setSelected(false));
      itemAPIs[next]?.setSelected(true);
      itemAPIs[next]?.focus();
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max((currentIndex < 0 ? 0 : currentIndex) - 1, 0);
      itemAPIs.forEach((api) => api.setSelected(false));
      itemAPIs[prev]?.setSelected(true);
      itemAPIs[prev]?.focus();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const index = currentIndex >= 0 ? currentIndex : 0;
      const item = currentItems[index];
      if (item) {
        // Ensure item is defined before processing
        const props = propsFromItem(item);
        if (props) handleSelect(props.id);
      }
      return;
    }

    if (e.key === "Escape") {
      // Delegate to modal if visible
      if (
        modalManager.isVisible() &&
        modalManager.getCurrentContent()?.contains(root)
      ) {
        modalManager.hide();
      }
    }
  };

  // Build DOM
  const headerHasContent = buildHeader();
  renderItems();
  if (headerHasContent) {
    root.appendChild(headerEl);
  }
  root.appendChild(itemsEl);
  if (footer) {
    footer.render(footerEl);
    root.appendChild(footerEl);
  }

  root.addEventListener("keydown", handleKey);

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    root.removeEventListener("keydown", handleKey);
    itemAPIs.forEach((api) => api.destroy());
    itemAPIs = [];
  };

  return {
    getElement: () => root,
    setItems,
    setSelectedId,
    getSelectedId,
    focusFirst,
    destroy,
  };
};
